import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { getDeposits } from '../../../../services/depositsService';
import { hydrateFilters } from '../../../../services/productService';
import { getSearchParams, handleSearchParams } from '../../../../helpers/handleSearchParams';
import { toYMD } from '../../../../helpers/dates';
import { BASE_FILTERS } from './constants';

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
const readYmd = (param) => {
  const raw = getSearchParams(param);
  return raw && YMD_RE.test(raw) ? raw : '';
};

/**
 * Campañas: Verano empieza el 1 de febrero e Invierno el 1 de septiembre
 * (criterio de Aitor). Inicio de la campaña en curso a una fecha dada: el
 * último de esos días que no es posterior a ella, en la zona del usuario.
 */
const CAMPAIGN_STARTS = [
  { month: 2, day: 1 },
  { month: 9, day: 1 },
];
const campaignStart = (base) => {
  const day = base.startOf('day');
  const candidates = [day.year - 1, day.year].flatMap((year) =>
    CAMPAIGN_STARTS.map(({ month, day: d }) => day.set({ year, month, day: d })),
  );
  return candidates.filter((c) => c <= day).sort((a, b) => b.toMillis() - a.toMillis())[0];
};
const ymdOf = (dt) => dt.toFormat('yyyy-MM-dd');
/** Inicio de la campaña en curso (`YYYY-MM-DD`). */
const currentCampaignStart = (base) => ymdOf(campaignStart(base));
/** Fin de la campaña anterior: el día antes del inicio de la actual. */
const lastCampaignEnd = (base) => ymdOf(campaignStart(base).minus({ days: 1 }));

/**
 * Atajos de fecha. Reciben la zona del usuario y devuelven `YYYY-MM-DD`
 * (día civil en esa zona). Todo el cálculo de fechas pasa por Luxon en
 * la zona del usuario, nunca por `new Date()` local.
 */
export const DATE_SHORTCUTS = [
  { key: 'today', label: 'Hoy', compute: () => '' },
  {
    key: 'lastMonthEnd',
    label: 'Cierre del mes anterior',
    compute: (tz) => DateTime.now().setZone(tz).startOf('month').minus({ days: 1 }).toFormat('yyyy-MM-dd'),
  },
  {
    key: 'lastYearEnd',
    label: 'Cierre del año anterior',
    compute: (tz) => DateTime.now().setZone(tz).startOf('year').minus({ days: 1 }).toFormat('yyyy-MM-dd'),
  },
  {
    key: 'campaignStart',
    label: 'Inicio de campaña actual',
    compute: (tz) => currentCampaignStart(DateTime.now().setZone(tz)),
  },
  {
    key: 'lastCampaignEnd',
    label: 'Fin de última campaña',
    compute: (tz) => lastCampaignEnd(DateTime.now().setZone(tz)),
  },
];

/**
 * Atajos de comparación, relativos a la fecha de valoración (o a hoy si
 * se valora el saldo vivo). Los de campaña: la campaña en curso EN la fecha
 * de valoración, no hoy.
 */
export const COMPARE_SHORTCUTS = [
  { key: 'yearAgo', label: 'Hace un año', compute: (base) => base.minus({ years: 1 }).toFormat('yyyy-MM-dd') },
  { key: 'monthAgo', label: 'Hace un mes', compute: (base) => base.minus({ months: 1 }).toFormat('yyyy-MM-dd') },
  {
    key: 'yearEnd',
    label: 'Cierre del año anterior',
    compute: (base) => base.startOf('year').minus({ days: 1 }).toFormat('yyyy-MM-dd'),
  },
  { key: 'campaignStart', label: 'Inicio de campaña actual', compute: currentCampaignStart },
  { key: 'lastCampaignEnd', label: 'Fin de última campaña', compute: lastCampaignEnd },
];

/**
 * Estado de los filtros del informe de existencias: fecha de valoración,
 * fecha de comparación, ámbito de depósitos y filtros de catálogo. Hidrata
 * los catálogos y sincroniza la URL (`persistInUrl`).
 *
 * Fechas en estado y URL como `YYYY-MM-DD` (día civil en la zona del
 * usuario). Vacío en `date` significa "saldo vivo ahora".
 */
export const useStocksFilters = (timezone) => {
  const [date, setDate] = useState(() => readYmd('date'));
  const [compareDate, setCompareDate] = useState(() => readYmd('compareDate'));

  // Ámbito de depósitos (unión de tres ejes), igual que en Eficacia. Aquí
  // el default del back es TODOS los depósitos operables, central incluido:
  // se valora todo el stock, no sólo el perímetro de venta.
  const [depositsValue, setDepositsValue] = useState(() => getSearchParams('deposits') || '');
  const [depositRolesValue, setDepositRolesValue] = useState(() => getSearchParams('depositRoles') || '');
  const [depositGroupsValue, setDepositGroupsValue] = useState(() => getSearchParams('depositGroups') || '');
  const [depositOptions, setDepositOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);

  const [filters, setFilters] = useState(BASE_FILTERS);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    Promise.all([
      hydrateFilters(
        Object.keys(BASE_FILTERS)
          .map((k) => BASE_FILTERS[k])
          .reduce((acc, f) => ({ ...acc, [f.slug]: { ...f } }), {}),
      ),
      getDeposits({ limit: 1000, excludeGroups: true }),
      getDeposits({ limit: 100, onlyGroups: true }),
    ]).then(([f, d, g]) => {
      setFilters((prev) => ({ ...prev, ...f }));
      setDepositOptions(d.data.data.map((dep) => ({ label: dep.name, value: dep.id })));
      setGroupOptions((g.data.data || []).map((grp) => ({ label: grp.name, value: grp.id })));
      setReady(true);
    });
  }, []);

  const setDepositScope = ({ all, roles, groups, deposits }) => {
    setDepositsValue(all ? 'ALL' : deposits.join(':'));
    setDepositRolesValue(all ? '' : roles.join(':'));
    setDepositGroupsValue(all ? '' : groups.join(':'));
  };

  const handleFilter = ({ slug, value }) => {
    setFilters((prev) => ({ ...prev, [slug]: { ...prev[slug], value } }));
  };

  const clearFilters = () => {
    setFilters((prev) => {
      const next = { ...prev };
      Object.keys(BASE_FILTERS).forEach((k) => {
        next[k] = { ...next[k], value: null };
      });
      return next;
    });
    setDepositsValue('');
    setDepositRolesValue('');
    setDepositGroupsValue('');
  };

  /** Fecha base para los atajos de comparación: la de valoración o hoy. */
  const baseDateTime = useMemo(
    () => (date ? DateTime.fromISO(date, { zone: timezone }) : DateTime.now().setZone(timezone)),
    [date, timezone],
  );

  const applyDateShortcut = (key) => {
    const s = DATE_SHORTCUTS.find((x) => x.key === key);
    if (s) setDate(s.compute(timezone));
  };

  const applyCompareShortcut = (key) => {
    const s = COMPARE_SHORTCUTS.find((x) => x.key === key);
    if (s) setCompareDate(s.compute(baseDateTime));
  };

  /** Setters que aceptan `Date` (pickers) o `YYYY-MM-DD` (inputs). */
  const setDateFromInput = (v) => setDate(v instanceof Date ? toYMD(v) || '' : v || '');
  const setCompareDateFromInput = (v) => setCompareDate(v instanceof Date ? toYMD(v) || '' : v || '');

  const persistInUrl = () => {
    handleSearchParams('date', date);
    handleSearchParams('compareDate', compareDate);
    handleSearchParams('deposits', depositsValue);
    handleSearchParams('depositRoles', depositRolesValue);
    handleSearchParams('depositGroups', depositGroupsValue);
    Object.keys(filters).forEach((k) => handleSearchParams(k, filters[k]?.value));
  };

  const validFilters = useMemo(
    () =>
      Object.keys(filters)
        .map((k) => (filters[k]?.value ? { slug: k, value: filters[k].value } : null))
        .filter(Boolean)
        .reduce((acc, f) => ({ ...acc, [f.slug]: f.value }), {}),
    [filters],
  );

  /**
   * Parámetros de fecha para la API: el back interpreta `YYYY-MM-DD` como
   * cierre del día civil mexicano. Si el usuario trabaja en Madrid el día
   * civil es el mismo texto, así que se manda tal cual y es el back quien
   * fija la hora de cierre (regla única para pantalla y PDFs).
   */
  const dateParams = useMemo(
    () => ({
      ...(date ? { date } : {}),
      ...(compareDate ? { compareDate } : {}),
    }),
    [date, compareDate],
  );

  const scopeParams = useMemo(
    () => ({
      ...(depositsValue ? { deposits: depositsValue } : {}),
      ...(depositRolesValue ? { depositRoles: depositRolesValue } : {}),
      ...(depositGroupsValue ? { depositGroups: depositGroupsValue } : {}),
    }),
    [depositsValue, depositRolesValue, depositGroupsValue],
  );

  return {
    date,
    setDate: setDateFromInput,
    compareDate,
    setCompareDate: setCompareDateFromInput,
    hasCompare: Boolean(compareDate),
    applyDateShortcut,
    applyCompareShortcut,
    depositsValue,
    depositRolesValue,
    depositGroupsValue,
    setDepositScope,
    depositOptions,
    groupOptions,
    filters,
    handleFilter,
    clearFilters,
    ready,
    validFilters,
    dateParams,
    scopeParams,
    persistInUrl,
  };
};
